<template><div><component-combinator component-name="DtToast" /><h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<code-example source-code='&lt;dt-toast
  title=&quot;Title&quot;
  :open=&quot;showToast&quot;
  :important=&quot;important&quot;
  :kind=&quot;selectedKind&quot;
  @close=&quot;closeEvent&quot;
&gt;
  Message body with
  &lt;dt-link&gt;
    a link
  &lt;/dt-link&gt;
  &lt;template #action&gt;
    &lt;dt-button
      :size=&quot;200&quot;
      importance=&quot;outlined&quot;
      kind=&quot;muted&quot;
    &gt;
      Action
    &lt;/dt-button&gt;
  &lt;/template&gt;
&lt;/dt-toast&gt;'>
<dt-stack direction="row" gap="200" class="d-w100p">
  <div class="d-fl-grow1">
    <dt-select-menu :show-label="false" label="Style" :options="toastOptions" v-model="selectedKind" />
  </div>
  <dt-checkbox value="important" @update:model-value="toggleImportant">Important</dt-checkbox>
  <dt-button @click="toggleToast">Toggle Example</dt-button>
</dt-stack>
</code-example>
<example-toast
  class="d-zi-notification"
  :open="showToast"
  title="Title"
  :important="important"
  :kind="selectedKind"
  @close="toggleToast"
/>
<h3 id="with-duration" tabindex="-1" class="d-docsite--header-3">With Duration <a class="header-anchor d-link d-docsite--link d-link" href="#with-duration" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">It's recommended to use a time of at least 6000 ms (minimum duration validated in the component) to give users enough time to read the toast. Take into account that the time necessary to read and comprehend the message could vary in users. For instance, users using assistive technology, or users with language barriers could potentially need more time to read and understand the message.
If the duration is not provided the toast won't disappear automatically.</p>
<code-example source-code='&lt;dt-toast
  title=&quot;Title&quot;
  :open=&quot;showDurationToast&quot;
  @close=&quot;closeEvent&quot;
  :duration=&quot;7500&quot;
&gt;
  Message body with
  &lt;dt-link&gt;
    a link
  &lt;/dt-link&gt;
  &lt;template #action&gt;
    &lt;dt-button
      :size=&quot;200&quot;
      importance=&quot;outlined&quot;
      kind=&quot;muted&quot;
    &gt;
      Action
    &lt;/dt-button&gt;
  &lt;/template&gt;
&lt;/dt-toast&gt;'>
<dt-button @click="toggleDurationToast(true)">Show Example</dt-button>
<example-toast
  class="d-zi-notification"
  :open="showDurationToast"
  title="Title"
  @close="toggleDurationToast(false)"
  @update:open="updateOpen"
  :duration="6000"
/>
</code-example>
<h3 id="with-self-positioning" tabindex="-1" class="d-docsite--header-3">With Self-Positioning <a class="header-anchor d-link d-docsite--link d-link" href="#with-self-positioning" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">If you need to self-position the toast at the top center, use the <code v-pre>d-toast-wrapper</code> Dialtone class:</p>
<code-example only-show="code" source-code='&lt;aside class=&quot;d-toast-wrapper&quot;&gt;
  &lt;dt-toast
    :title=&quot;title&quot;
    :message=&quot;message&quot;
    :open=&quot;isShown&quot;
  &gt;&lt;/dt-toast&gt;
&lt;/aside&gt;'>
<aside class="d-toast-wrapper">
  <dt-toast
    :title="title"
    :message="message"
    :open="isShown"
  ></dt-toast>
</aside>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="toast" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="toast" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Avoid using toast for critical information since toast disappears automatically and make sure
to provide enough time to read the message and act consequently. For best accessible user experience, the amount of
time a toast displays for should be user configurable.</p>
<p class="d-docsite--paragraph">Using <code v-pre>role=&quot;alert&quot;</code>, it sets <code v-pre>aria-live=&quot;assertive&quot;</code> which
means it will immediately interrupt anything currently being read by the screen reader, so use it for things
that require immediate attention such as:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">An invalid value was entered into a form field</li>
<li class="d-docsite--list-element">The user's login session is about to expire</li>
<li class="d-docsite--list-element">The connection to the server was lost, local changes will not be saved</li>
</ul>
<p class="d-docsite--paragraph">Meanwhile <code v-pre>role=&quot;status&quot;</code> implies <code v-pre>aria-live=&quot;polite&quot;</code> which
means the toast will be read out after what's currently being has finished.</p>
<p class="d-docsite--paragraph">A screen reader visible only close button is added by default.</p>
<component-accessible-table component-name="toast" /></div></template>


<script setup>
import ExampleToast from '@exampleComponents/ExampleToast.vue';
import { ref } from 'vue';

const toastOptions = [
  { value: 'base', label: 'Base' },
  { value: 'critical', label: 'Critical' },
  { value: 'info', label: 'Info' },
  { value: 'positive', label: 'Positive' },
  { value: 'warning', label: 'Warning' },
];
const showToast = ref(false);
const important = ref(false);
const pinned = ref(false);
const selectedKind = ref('base');
const showDurationToast = ref(false);

function toggleToast () {
  showToast.value = !showToast.value;
}
function toggleDurationToast (value) {
  showDurationToast.value = value;
}
const updateOpen = (value) => {
  if (!value) showDurationToast.value = false;
};

function toggleImportant (checked) {
  important.value = checked;
}
function togglePinned () {
  pinned.value = !pinned.value;
}
</script>